import Prismic from 'prismic-javascript';
import {DefaultClient} from 'prismic-javascript/types/client';

/** @module utils/CMSUtils */

/**
 * Interface for page data parameters
 */
interface PageData {
    slug: string;
    customType: string;
    cookies: Record<string, string | undefined>;
}

/**
 * Interface for returned page data
 */
interface PageResultData {
    [key: string]: unknown;
}

let client: DefaultClient | undefined;

const CMSUtils = {
    /**
     * Initializes the Prismic client
     * @static
     * @param apiEndpoint - The Prismic API endpoint
     */
    init(apiEndpoint = 'https://spothero.prismic.io/api/v2'): void {
        client = Prismic.client(apiEndpoint);
    },

    /**
     * Gets the page data from Prismic
     * @static
     * @function getPageData
     * @param params - Object containing `slug`, `customType`, and `cookies`
     * @return Promise resolving to the page data or throws an error
     */
    async getPageData({
        slug,
        customType,
        cookies,
    }: PageData): Promise<PageResultData> {
        if (!client) {
            throw new Error(
                'Prismic client has not been initialized. Call `init` first.'
            );
        }

        let cleanedSlug = slug;

        if (slug.includes('/')) {
            const path = slug.split('/').filter(Boolean);

            cleanedSlug = path[path.length - 1];
        }

        const prismicCookie = cookies?.['io.prismic.preview']
            ? (() => {
                  try {
                      return JSON.parse(
                          cookies['io.prismic.preview'] as string
                      );
                  } catch (e) {
                      console.warn(
                          'Failed to parse the preview cookie. Falling back to default behavior.',
                          e
                      );

                      return null;
                  }
              })()
            : null;

        const previewCookie =
            prismicCookie?.['spothero.prismic.io']?.preview || null;

        try {
            const result = await client.query(
                Prismic.Predicates.at(`my.${customType}.uid`, cleanedSlug),
                {
                    ...(previewCookie && {ref: previewCookie}),
                }
            );

            if (!result?.results?.length) {
                throw Error(
                    `Page with slug ${slug} does not exist in Prismic data`
                );
            }

            return result.results[0].data;
        } catch (error) {
            throw error;
        }
    },
};

export default CMSUtils;
